/* eslint-disable camelcase */
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormat, parseSecondsToHM, secondsToMinutes, secondsToTime } from '@nx-smartmonkey/shared/helpers';
import * as XLSX from 'xlsx';

import { Driver } from '../../../../domain/driver/Driver';
import { Plan } from '../../../../domain/plan/Plan';
import { Project } from '../../../../domain/project/Project';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { floatToFixedDecimals } from '../../../../helpers/excel/Common';
import { customFieldToFormatter, ExcelEntryFormatter, jsonToExcel } from '../../../../helpers/excel/Formatters';
import {
  parseList,
  parseStringArray,
  parseTimewindowsToExcel,
  parseTimewindowToExcel,
} from '../../../../helpers/excel/Parsers';
import { ExtendedStop, extendStops } from './Stops';

// PlanStopHeaders
const getDriverFormatters = (): ExcelEntryFormatter[] => [
  {
    id: `name`,
    header: intl.formatMessage({ id: `routes.excel.columns.name` }),
    formatter: (driver: Driver) => driver.label,
  },
  {
    id: `external_id`,
    header: intl.formatMessage({ id: `routes.excel.columns.external_id` }),

    formatter: (driver: Driver) => driver.external_id,
  },
  {
    id: `location_start`,
    header: intl.formatMessage({ id: `routes.excel.columns.location_start` }),
    formatter: (driver: Driver) => driver.start_location?.label || ``,
  },
  {
    id: `address_start`,
    header: intl.formatMessage({ id: `routes.excel.columns.address_start` }),

    formatter: (driver: Driver) => driver.start_location?.label || ``,
  },
  {
    id: `location_end`,
    header: intl.formatMessage({ id: `routes.excel.columns.location_end` }),
    formatter: (driver: Driver) => driver.end_location?.label || ``,
  },
  {
    id: `address_end`,
    header: intl.formatMessage({ id: `routes.excel.columns.address_end` }),
    formatter: (driver: Driver) => driver.end_location?.label || ``,
  },
  {
    id: `time_window`,
    header: intl.formatMessage({ id: `routes.excel.columns.time_window` }),
    formatter: (driver: Driver) => (driver.time_window ? parseTimewindowToExcel(driver.time_window) : ``),
  },
  // max_distance: null,
  {
    id: `max_weight`,
    header: intl.formatMessage({ id: `routes.excel.columns.max_weight` }),

    formatter: (driver: Driver) => driver.max_weight,
    type: `n`,
  },
  {
    id: `max_volume`,
    header: intl.formatMessage({ id: `routes.excel.columns.max_volume` }),
    formatter: (driver: Driver) => driver.max_volume,
    type: `n`,
  },
  {
    id: `max_services`,
    header: intl.formatMessage({ id: `routes.excel.columns.max_services` }),
    type: `n`,
  },
  {
    id: `provides`,
    header: intl.formatMessage({ id: `routes.excel.columns.provides` }),
    formatter: (driver: Driver) => parseList(driver.provides),
    type: `n`,
  },
  // TODO: Missing link
  // {
  //   id: `route_link`,
  //   header: intl.formatMessage({ id: `routes.excel.columns.route_link` }),
  //   formatter: (driver: Driver) => driver.getRoutePublicLink(activeProject.public_key),
  //   type: `l`,
  // },
  {
    id: `price_per_minute`,
    header: intl.formatMessage({ id: `routes.excel.columns.price_per_minute` }),
    formatter: (driver: Driver) => driver.price_per_minute,
    type: `n`,
  },
  {
    id: `price_per_distance`,
    header: intl.formatMessage({ id: `routes.excel.columns.price_per_distance` }),
    formatter: (driver: Driver) => driver.price_per_distance,
    type: `n`,
  },
  // TODO: Missing link
  {
    id: `total_price`,
    header: intl.formatMessage({ id: `routes.excel.columns.total_price` }),
    type: `n`,
  },
  {
    id: `fuel_type`,
    header: intl.formatMessage({ id: `routes.excel.columns.fuel_type` }),
    formatter: (driver: Driver) =>
      driver.fuel_type && intl.formatMessage({ id: `vehicle.fuel_type.${driver.fuel_type}` }),
  },
  {
    id: `consumption`,
    header: intl.formatMessage({ id: `routes.excel.columns.consumption` }),
    formatter: (driver: Driver) => driver.consumption,
    type: `n`,
  },
  {
    id: `total_distance`,
    header: intl.formatMessage({ id: `routes.excel.columns.total_distance` }),
    type: `n`,
  },
  {
    id: `assigned_volume`,
    header: intl.formatMessage({ id: `routes.excel.columns.assigned_volume` }),
    formatter: (driver: Driver) => driver.getAssignedVolume(),
    type: `n`,
  },
  {
    id: `assigned_weight`,
    header: intl.formatMessage({ id: `routes.excel.columns.assigned_weight` }),
    formatter: (driver: Driver) => driver.getAssignedWeight(),
    type: `n`,
  },
  {
    id: `start_date`,
    header: intl.formatMessage({ id: `routes.excel.columns.start_date` }),
    formatter: (driver: Driver) => (driver.start_date ? dateFormat(driver.start_date) : ``),
    type: `d`,
  },
  {
    id: `planned_start_time`,
    header: intl.formatMessage({ id: `routes.excel.columns.planned_start_time` }),
    formatter: (driver: Driver) => parseSecondsToHM(driver.planned_start_time),
    type: `n`,
  },
  {
    id: `end_date`,
    header: intl.formatMessage({ id: `routes.excel.columns.end_date` }),
    formatter: (driver: Driver) => (driver.end_date ? dateFormat(driver.end_date) : ``),
    type: `d`,
  },
  {
    id: `planned_end_time`,
    header: intl.formatMessage({ id: `routes.excel.columns.planned_end_time` }),
    formatter: (driver: Driver) => parseSecondsToHM(driver.planned_end_time),
    type: `n`,
  },
];

const getStopFormatters = ({ units, drivers }: Project): ExcelEntryFormatter[] => {
  const volumeIn = units!.volume ?? ``;
  const weightIn = units!.weight ?? ``;
  const distanceIn = units!.distance ?? ``;
  return [
    {
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.driver_id` }),
      id: `stop_id`,
      formatter: (stopDriver: ExtendedStop) => stopDriver[1]?.label,
    },
    {
      id: `order`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.order` }),
      formatter: (stopDriver: ExtendedStop) => (stopDriver[0].order !== undefined ? stopDriver[0].order + 1 : ``),
    },
    {
      id: `external_id`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.external_id` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].external_id,
    },
    {
      id: `client_external_id`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.client_external_id` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].client_external_id,
    },
    {
      id: `name`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.name` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].label,
    },
    {
      id: `location`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.location` }),
      formatter: (stopDriver: ExtendedStop) =>
        stopDriver[0].location ? `${stopDriver[0].location?.lat}, ${stopDriver[0].location?.lng}` : ``,
    },
    {
      id: `address`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.address` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].location?.label || ``,
    },
    {
      id: `address_2`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.address_2` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].location_details,
    },
    {
      id: `report_location`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report_location` }),
      formatter: (stopDriver: ExtendedStop) => {
        if (!stopDriver[0].reports) return ``;

        const report = stopDriver[0].getCurrentReport();
        if (report?.location?.isValid()) return report.location.toCoordinate().toString();

        return ``;
      },
    },
    ...(drivers.configHasField(`stop_duration`)
      ? [
          {
            id: `report_time_in_stop`,
            header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report_time_in_stop` }),
            formatter: (stopDriver: ExtendedStop) => {
              if (!stopDriver[0].reports) return undefined;

              const report = stopDriver[0].getCurrentReport();
              if (report?.time_in_stop === undefined) {
                return undefined;
              }
              return secondsToTime(report.time_in_stop);
            },
          },
        ]
      : []),
    {
      id: `time_windows`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.time_windows` }),
      formatter: (stopDriver: ExtendedStop) =>
        stopDriver[0].time_windows ? parseTimewindowsToExcel(stopDriver[0].time_windows) : ``,
    },
    {
      id: `weight`,
      header: `${intl.formatMessage({ id: `routes.excel.vehicles.columns.weight` })} (${weightIn})`,
      formatter: (stopDriver: ExtendedStop) => {
        if (stopDriver[0].weight === undefined) {
          return undefined;
        }

        return stopDriver[0].weight;
      },
      type: `n`,
    },
    {
      id: `volume`,
      header: `${intl.formatMessage({ id: `routes.excel.vehicles.columns.volume` })} (${volumeIn})`,
      formatter: (stopDriver: ExtendedStop) => {
        if (stopDriver[0].volume === undefined) {
          return undefined;
        }

        return stopDriver[0].volume;
      },
      type: `n`,
    },
    {
      id: `requires`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.requires` }),
      formatter: (stopDriver: ExtendedStop) => parseStringArray(stopDriver[0].requires ?? []),
    },
    {
      id: `eta`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.eta` }),
      formatter: (stopDriver: ExtendedStop) =>
        stopDriver[0].planned_arrival_time ? parseSecondsToHM(stopDriver[0].planned_arrival_time) : ``,
    },
    {
      id: `etd`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.etd` }),
      formatter: (stopDriver: ExtendedStop) =>
        stopDriver[0].planned_departure_time ? parseSecondsToHM(stopDriver[0].planned_departure_time) : ``,
    },
    {
      id: `duration`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.duration` }),
      formatter: (stopDriver: ExtendedStop) => secondsToMinutes(stopDriver[0].duration),
      type: `n`,
    },
    {
      id: `distance`,
      header: `${intl.formatMessage({ id: `routes.excel.vehicles.columns.distance` })} (${distanceIn})`,
      formatter: (stopDriver: ExtendedStop) => {
        if (stopDriver[0].order !== undefined) {
          return floatToFixedDecimals((stopDriver[0].distance_to_next_stop ?? 0) / 1000, 2);
        }
        return undefined;
      },
      type: `n`,
    },
    {
      id: `comments`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.comments` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].comments,
    },
    {
      id: `reference_person`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.reference_person` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].reference_person,
    },
    {
      id: `phone`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.phone` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].phone,
    },
    {
      id: `email`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.email` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].email,
    },
    {
      id: `url`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.url` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].url,
      type: `l`,
    },
    {
      id: `type`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.type` }),
      formatter: (stopDriver: ExtendedStop) => stopDriver[0].type,
    },
    {
      id: `report_created_at`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report_created_at` }),
      type: `d`,
      formatter: (stopDriver: ExtendedStop) => {
        if (!stopDriver[0].reports) {
          return ``;
        }
        const report = stopDriver[0].getCurrentReport();
        if (report) {
          return report.created_at ? dateFormat(report.created_at) : undefined;
        }
        return ``;
      },
    },
    {
      id: `status`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.status` }),
      formatter: (stopDriver: ExtendedStop) =>
        intl.formatMessage({ id: `routes.excel.vehicles.columns.status.${stopDriver[0].status}` }),
    },
    {
      id: `feedback_comments`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report.comments` }),
      formatter: (stopDriver: ExtendedStop) => {
        const report = stopDriver[0].getCurrentReport();
        if (report) {
          return report.comments;
        }
        return ``;
      },
    },
    {
      id: `feedback_signature`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report.signature` }),
      formatter: (stopDriver: ExtendedStop) => {
        const report = stopDriver[0].getCurrentReport();
        if (report) {
          return !!report.signature;
        }
        return undefined;
      },
      type: `b`,
    },
    {
      id: `feedback_images`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report.images` }),
      formatter: (stopDriver: ExtendedStop) => {
        const report = stopDriver[0].getCurrentReport();
        if (report) {
          return report.images?.map((image) => `${image.url}/public`).join(`, `);
        }
        return ``;
      },
    },
    {
      id: `feedback_reasons`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.report.reasons` }),
      formatter: (stopDriver: ExtendedStop) => {
        const report = stopDriver[0].getCurrentReport();
        if (report && report.cancel_reason) {
          return intl.formatMessage({ id: `properties.stop.report.reason.${report.cancel_reason}` });
        }
        return ``;
      },
    },
  ];
};

export const exportPlan = ({
  plan,
  activeProject,
  customFields = [],
}: {
  plan: Plan;
  activeProject: Project;
  customFields: CustomFieldDefinition[];
}) => {
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: `${plan.label}`,
    Subject: `Export for plan ${plan.label}`,
    Author: `Routal`,
    CreatedDate: new Date(),
  };

  const driverCustomFieldFormatter: ExcelEntryFormatter[] = customFields
    .filter((cf) => cf.field_model === `vehicle` && activeProject.planner.vehicleHasCustomField(cf.custom_id))
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (driver: Driver) => formatter.formatter && formatter.formatter(driver.custom_fields),
      };
    });

  // Add all stops sheet
  const allDriverFormatters: ExcelEntryFormatter[] = [
    ...getDriverFormatters().map((driverFormatter) => {
      let newFormatter = {};

      switch (driverFormatter.id) {
        case `total_price`:
          newFormatter = { formatter: (driver: Driver) => driver.getTotalPriceWithUnits(activeProject) };
          break;
        case `total_distance`:
          newFormatter = { formatter: (driver: Driver) => driver.getTotalDistanceWithUnits(activeProject) };
          break;
      }

      return {
        ...driverFormatter,
        ...newFormatter,
      };
    }),
    ...driverCustomFieldFormatter,
  ];
  const driversSheet = jsonToExcel(plan.drivers, allDriverFormatters);

  const stopCustomFieldFormatter = customFields
    .filter((cf) => cf.field_model === `client` && activeProject.planner.stopHasCustomField(cf.custom_id))
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (extendedStop: ExtendedStop) =>
          formatter.formatter && formatter.formatter(extendedStop[0].custom_fields),
      };
    });

  // CustomFieldFormatters always access {object}.custom_fields,
  // since now we want to deep into {object}.reports[0].custom_fields
  // we need to create a function on top of the original function
  // that will access the right custom_fields
  const reportCustomFieldFormatter: ExcelEntryFormatter[] = customFields
    .filter((cf) => cf.field_model === `service_report_canceled` || cf.field_model === `service_report_completed`)
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (extendedStop: ExtendedStop) => {
          return (
            extendedStop[0].reports &&
            formatter.formatter &&
            formatter.formatter(extendedStop[0].getCurrentReport()?.custom_fields)
          );
        },
      };
    });

  // Add all stops sheet
  const allStopsFormatter = [
    ...getStopFormatters(activeProject),
    ...stopCustomFieldFormatter,
    ...reportCustomFieldFormatter,
  ];

  const extendedStops = extendStops(plan.stops, plan.drivers);
  const stopsSheet = jsonToExcel(extendedStops, allStopsFormatter);

  const stopsLabel = intl.formatMessage({ id: `routes.excel.sheets.stops` });
  const vehiclesLabel = intl.formatMessage({ id: `routes.excel.sheets.vehicles` });
  wb.SheetNames.push(stopsLabel);
  wb.Sheets[stopsLabel] = stopsSheet;
  wb.SheetNames.push(vehiclesLabel);
  wb.Sheets[vehiclesLabel] = driversSheet;

  // Add stops by driver sheet
  plan.drivers?.forEach((driver, idx) => {
    const sheetName = `${idx} - ${(driver.label || ``).replace(/[\\/*[\]:?]/g, `-`)}`.substring(0, 20);
    wb.SheetNames.push(sheetName);
    wb.Sheets[sheetName] = jsonToExcel(extendStops(driver.stops, [driver]), allStopsFormatter);
  });

  // Add unassigned stops sheet
  const unassignedStopsSheet = jsonToExcel(
    extendedStops.filter((stopDriver) => !stopDriver[0].driver_id),
    allStopsFormatter
  );
  wb.Sheets[`Unassigned Stops`] = unassignedStopsSheet;

  return XLSX.write(wb, { bookType: `xlsx`, type: `binary`, cellDates: true });
};
